import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";

import styles from "./_styles/Choose.module.scss";
import { useAppSelector } from "../../store/store";
import { useGetSearchFilmsListQuery } from "../../store/api/filmAPI";
import { ISectionItem } from "./_types/interfaces";
import MobilePlayer from "./_components/section/MobilePlayer";
import DesctopPlayer from "./_components/section/DesctopPlayer";
import Footer from "../../components/footer/Footer";
import SideBar from "../../components/sideBar/SideBar";
import Header from "../../components/header/Header";
import { useDispatch } from "react-redux";
import ArrowUp from "../../components/icons/forYou/ArrowUp";
import ArrowDown from "../../components/icons/forYou/ArrowDown";
import { ROUTES } from "../../constants/routes";
import { setLastVisitedId } from "../../store/slices/currentItem.slice";
import { useNavigate } from "react-router-dom";
import FiltersButton from "./_components/FiltersHeader/FiltersButton";
import Loader from "../../components/loader/Loader";
import useWindowSize from "../../hooks/useWindowSize";
import { useTranslation } from "react-i18next";
// import TipsComponent from "../../components/icons/tips/TipsComponent";
import ShareModal from "../../components/shared/ShareModal/ShareModal";
import OnboardingTour from "../../components/onboardingTour/OnboardingTour";
import { languageHandler, shuffleMoviesWithFixedCommercials } from "../../utils/helpers";
import { setLoginModal } from "../../store/slices/modals.slice";
import { setPage } from "../../store/slices/lists.slice";
import {
  cleanUpFeedPage,
  filterBrokenMovie,
  setCountFeed,
  setDataToFeedList,
  setFeedPage,
  setPrevFeedPage,
} from "../../store/slices/feed.slice";
import useShareClick from "../../hooks/useShareClick";
import useScreenOrientation from "../../utils/hooks/useScreenOrientation";
import LoginModal from "../../components/shared/LoginModal/LoginModal";
import useFeed from "../../hooks/useFeed";

// TODO NEED MOVE STATES FROM COMPONENT TO INTERFACE PROVIDER AND REMOVE HERE

const FEED_DESKTOP_SCROLL_OFFSET = 0;

const isSpaceKey = (event: KeyboardEvent) => (
  event.code === "Space" || event.key === " " || event.key === "Spacebar"
);

const scrollFeedItemIntoView = (item: HTMLElement, isMobile: boolean, behavior: ScrollBehavior = "smooth") => {
  const container = document.getElementById("choose_section") as HTMLElement | null;

  if (!isMobile && container) {
    container.focus({ preventScroll: true });
    container.scrollTo({
      top: item.offsetTop + FEED_DESKTOP_SCROLL_OFFSET,
      behavior,
    });
    return;
  }

  item.scrollIntoView({ behavior, block: "start", inline: "nearest" });
};

const Choose = () => {
  const [guide, setGuide] = useState(false);

  const { height, horizontal } = useScreenOrientation();

  const onTrailerWatched = (_trailerTitle: string, _trailerUrl: string) => { };

  const page = useAppSelector((state: any) => state.feedList.page);
  const prevPage = useAppSelector((state: any) => state.feedList.prevPage);
  const moviesList = useAppSelector((state: any) => state.feedList.commonList);
  const userData = useAppSelector((state: any) => state.auth.userData);

  const { genres, period } = useAppSelector((state: any) => state.filters);
  const muted = useAppSelector((state: any) => state.filters.mutedState);
  const currentId = useAppSelector((state: any) => state.item.currentVideo);
  const isAds = useAppSelector((state: any) => state.item.isAds);
  const lastVisited = useAppSelector((state: any) => state.item.lastVisited);
  const { shareModal, loginModal } = useAppSelector((state: any) => state.modals);

  const ref = useRef<HTMLElement | null>(null);

  const { shareList, shareHandler } = useShareClick();

  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const { windowSize } = useWindowSize();
  const isMobile = windowSize === "mobile";
  const { sortBy, quickFilter } = useFeed();

  const queryHandler = () => {
    if (genres.length) {
      return {
        page: page,
        language: languageHandler(i18n.language),
        with_genres: genres.length ? genres.toString() : "",
        release_date_gte: `${period[0]}-01-01`,
        release_date_lte: `${period[1]}-01-01`,
        include_video: "true",
        site: "YouTube",
        sort_by: sortBy,
      };
    }

    if (!genres.length) {
      return {
        page: page,
        language: languageHandler(i18n.language),
        include_video: "true",
        site: "YouTube",
        sort_by: sortBy,
      };
    }
  };

  const { data, isLoading, isFetching, refetch } = useGetSearchFilmsListQuery({
    mainFilter: "/movies/sandbox",
    queries: {
      ...queryHandler(),
      page: page,
    },
    noCache: userData?.has_monthly_subscription || userData?.has_yearly_subscription,
  });

  const prevEmailRef = useRef(userData.email);
  useEffect(() => {
    if (userData.email && userData.email !== prevEmailRef.current) {
      dispatch(cleanUpFeedPage());
      refetch();
    }
    prevEmailRef.current = userData.email;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData.email]);

  useEffect(() => {
    // if (!localStorage.getItem("guide")) {
    //   setGuide(true);
    // }

    // const handleStorageChange = () => {
    //   const savedData = localStorage.getItem("guide");
    //   if (savedData === "1") {
    //     setGuide(false);
    //   }
    // };

    // window.addEventListener("storage", handleStorageChange);

    // return () => {
    //   window.removeEventListener("storage", handleStorageChange);
    // };
    setGuide(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isFetching && data?.page > prevPage) {
      dispatch(setDataToFeedList(shuffleMoviesWithFixedCommercials(data?.results)));
      dispatch(setPrevFeedPage(data?.page));
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, isFetching]);

  const setPageHandler = () => {
    dispatch(setFeedPage(1));
  };

  const arrowNavHandler = (direction: string, id: number) => {
    const currentIndex = moviesList.findIndex((item: any) => item.id === id);
    const item = document.getElementById(moviesList[direction === "up" ? currentIndex - 1 : currentIndex + 1]?.id);

    if (item) {
      scrollFeedItemIntoView(item, isMobile);
    }
  };

  const filterErrorItem = useCallback(
    (id: number) => {
      const currentIndex = moviesList.findIndex((item: any) => item.id === id);
      const nextItem = moviesList[currentIndex + 1] || moviesList[currentIndex - 1];
      const nextElement = nextItem ? document.getElementById(String(nextItem.id)) : null;

      if (nextElement) {
        scrollFeedItemIntoView(nextElement, isMobile);
      }

      window.setTimeout(() => {
        dispatch(filterBrokenMovie(id));
      }, nextElement ? 300 : 0);
    },
    [dispatch, isMobile, moviesList],
  );

  const goToFilters = () => {
    navigate(ROUTES.FILTERS);
    dispatch(setLastVisitedId(currentId));
  };

  useEffect(() => {
    if (lastVisited !== 0 && moviesList) {
      const item = document.getElementById(String(lastVisited));
      if (item) {
        setTimeout(() => {
          item.scrollIntoView({ behavior: "auto", block: "start", inline: "nearest" });
        }, 100);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastVisited]);

  useEffect(() => {
    setPage(1);
  }, [genres, quickFilter]);

  useEffect(() => {
    const elem = document.getElementById("choose_section");
    if (isMobile && horizontal && elem) {
      elem.style.overflowY = "hidden";
    }

    if (isMobile && !horizontal && elem) {
      elem.style.overflowY = "scroll";
    }
  }, [horizontal, isMobile]);

  useEffect(() => {
    const elem = document.getElementById("choose_section");

    if (elem && !isAds) {
      elem.style.overflowY = "scroll";
    }
    if (elem && isAds) {
      setTimeout(() => {
        elem.style.overflowY = "hidden";
      }, 200);
    }

    if (!isMobile) {
      const handleWheel = (event: any) => {
        event.preventDefault();
      };

      let preventSpaceKeyUp = false;

      const isCurrentAd = () => {
        const currentMovie = moviesList.find((item: any) => item.id === currentId);
        return isAds || Boolean(currentMovie?.commercial_url);
      };

      const keepCurrentAdInView = () => {
        const itemElement = document.getElementById(currentId);
        if (itemElement) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setTimeout(() => {
                scrollFeedItemIntoView(itemElement, isMobile);
              }, 100);
            });
          });
        }
      };

      const scrollToNextTrailer = () => {
        const currentIndex = moviesList.findIndex((item: any) => item.id === currentId);
        const item = document.getElementById(moviesList[currentIndex + 1]?.id);

        if (item) {
          scrollFeedItemIntoView(item, isMobile);
        }
      };

      const handleSpaceKeyDown = (event: KeyboardEvent) => {
        if (!isSpaceKey(event)) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        preventSpaceKeyUp = true;

        if (isCurrentAd()) {
          keepCurrentAdInView();
          return;
        }

        if (!event.repeat) {
          scrollToNextTrailer();
        }
      };

      const handleSpaceKeyUp = (event: KeyboardEvent) => {
        if (!preventSpaceKeyUp || !isSpaceKey(event)) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        preventSpaceKeyUp = false;
      };

      const handleSpaceKeyPress = (event: KeyboardEvent) => {
        if (!preventSpaceKeyUp || !isSpaceKey(event)) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
      };

      const handleKeyDown = (event: any) => {
        if (isCurrentAd()) {
          event.preventDefault();
          keepCurrentAdInView();
          return;
        }

        if (event.key === "ArrowUp" || event.key === "ArrowDown" || isSpaceKey(event)) {
          return;
        }
        event.preventDefault();
      };

      document.addEventListener("wheel", handleWheel, { passive: false });
      window.addEventListener("keydown", handleSpaceKeyDown, true);
      window.addEventListener("keypress", handleSpaceKeyPress, true);
      window.addEventListener("keyup", handleSpaceKeyUp, true);
      document.addEventListener("keydown", handleKeyDown);

      return () => {
        document.removeEventListener("wheel", handleWheel);
        window.removeEventListener("keydown", handleSpaceKeyDown, true);
        window.removeEventListener("keypress", handleSpaceKeyPress, true);
        window.removeEventListener("keyup", handleSpaceKeyUp, true);
        document.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [currentId, isMobile, isAds, moviesList]);

  const snapItemsSelector = useMemo(() => "[data-snap-item='true']", []);

  useEffect(() => {
    if (!isMobile || horizontal || isAds) return;

    const container = document.getElementById("choose_section") as HTMLElement | null;
    if (!container) return;

    let scrollEndTimer: number | undefined;
    let rafId: number | null = null;
    let isSnapping = false;

    const getItems = () => Array.from(container.querySelectorAll<HTMLElement>(snapItemsSelector));

    const snapToNearest = (behavior: ScrollBehavior = "smooth") => {
      if (isSnapping) return;
      const items = getItems();
      if (!items.length) return;

      const currentTop = container.scrollTop;
      let nearestTop = items[0].offsetTop;
      let nearestDist = Math.abs(nearestTop - currentTop);

      for (let i = 1; i < items.length; i++) {
        const top = items[i].offsetTop;
        const dist = Math.abs(top - currentTop);
        if (dist < nearestDist) {
          nearestDist = dist;
          nearestTop = top;
        }
      }

      // If we're already very close, do nothing (prevents jitter)
      if (nearestDist < 2) return;

      isSnapping = true;
      container.scrollTo({ top: nearestTop, behavior });

      // Release snapping lock after scroll settles
      window.setTimeout(() => {
        isSnapping = false;
      }, 250);
    };

    const scheduleSnap = () => {
      if (scrollEndTimer) window.clearTimeout(scrollEndTimer);
      scrollEndTimer = window.setTimeout(() => {
        // Wait one frame to let inertia settle
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => snapToNearest("smooth"));
      }, 120);
    };

    const onScroll = () => {
      scheduleSnap();
    };

    const onTouchStart = () => {
      if (scrollEndTimer) window.clearTimeout(scrollEndTimer);
      isSnapping = false;
    };

    const onTouchEnd = () => {
      scheduleSnap();
    };

    container.addEventListener("scroll", onScroll, { passive: true });
    container.addEventListener("touchstart", onTouchStart, { passive: true });
    container.addEventListener("touchend", onTouchEnd, { passive: true });
    container.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      container.removeEventListener("scroll", onScroll);
      container.removeEventListener("touchstart", onTouchStart);
      container.removeEventListener("touchend", onTouchEnd);
      container.removeEventListener("touchcancel", onTouchEnd);
      if (scrollEndTimer) window.clearTimeout(scrollEndTimer);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [horizontal, isAds, isMobile, snapItemsSelector]);

  useEffect(() => {
    if (currentId && moviesList.length) {
      const currentIndex = moviesList.findIndex((item: any) => item.id === currentId);
      if (currentIndex === moviesList.length - 5) {
        setPageHandler();
      }

      // @ts-ignore
      ref.current?.focus();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId, moviesList]);

  const SOFT_WALL_LIMIT = 4;
  const SOFT_WALL_KEY = "trailerViewCount";

  useEffect(() => {
    if (!currentId || isAds) return;
    const token = localStorage.getItem("accessToken");
    if (token) return;

    const count = parseInt(localStorage.getItem(SOFT_WALL_KEY) || "0", 10) + 1;
    localStorage.setItem(SOFT_WALL_KEY, String(count));

    if (count >= SOFT_WALL_LIMIT) {
      dispatch(setLoginModal("softWallTitle"));
    }
  }, [currentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateHareCount = (key: any, type: any) => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;

    dispatch(setCountFeed({ id: currentId, key, type }));
  };

  const paddingBasedOnResolution = "100px 0 83px 0";

  return moviesList ? (
    <Suspense fallback={null}>
      <section
        className={styles.mobileContainer}
        style={{ height: `${height}px` }}
        ref={ref}
        tabIndex={0}
        id={"choose_section"}
      >
        {(!horizontal || !isMobile) && <Header className={styles.headerWrapper} />}
        <SideBar />
        {(!horizontal || !isMobile) && <Footer source={""} />}
        {!isLoading &&
          !guide &&
          moviesList?.map((item: ISectionItem, index: number) => (
            <div
              key={`${item.id}_${index}`}
              id={`${item.id}`}
              data-snap-item="true"
              className={isMobile ? styles.item : styles.itemDesctop}
              style={{
                height: `${height}px`,
                overflow: "hidden",
                padding: isMobile ? (horizontal ? 0 : undefined) : paddingBasedOnResolution,
              }}
            >
              {isMobile ? (
                <MobilePlayer
                  item={item}
                  horizontal={horizontal}
                  currentId={currentId}
                  filterErrorItem={filterErrorItem}
                  skipAdsHandler={arrowNavHandler}
                  onTrailerWatched={onTrailerWatched}
                />
              ) : (
                <DesctopPlayer
                  muted={muted}
                  item={item}
                  horizontal={horizontal}
                  filterErrorItem={filterErrorItem}
                  skipAdsHandler={arrowNavHandler}
                  onTrailerWatched={onTrailerWatched}
                />
              )}
            </div>
          ))}
        {!isMobile && (
          <div className={styles.navFiltersWrapper}>
            <FiltersButton goToFilters={goToFilters} />
            <div className={styles.playerRemoteController}>
              <div
                className={styles.navArrowWrapper}
                onClick={() => {
                  if (!isAds) {
                    arrowNavHandler("up", currentId);
                  }
                }}
              >
                <ArrowUp className={styles.arrowIcon} />
              </div>
              <div
                className={`${styles.navArrowWrapper} ${styles.arrowDown}`}
                onClick={() => {
                  if (!isAds) {
                    arrowNavHandler("down", currentId);
                  }
                }}
              >
                <ArrowDown className={styles.arrowIcon} />
              </div>
            </div>
          </div>
        )}
      </section>
      {shareModal && (
        <ShareModal
          currentId={currentId}
          shared={shareList.data?.results?.includes(data?.id)}
          shareHandler={shareHandler}
          updateFieldLocal={updateHareCount}
        />
      )}
      {loginModal && <LoginModal />}
      {/* {guide && <TipsComponent setGuide={setGuide}/>} */}
      <OnboardingTour />
    </Suspense>
  ) : (
    <div className={styles.preloaderWrapper}>
      <Loader />
    </div>
  );
};

export default Choose;
